import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Star, Loader2, Pencil, Trash2, Plus, Upload, StarOff,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Database } from "@/integrations/supabase/types";
import { AdminShell } from "@/components/admin/AdminShell";

type GalleryRow = Database["public"]["Tables"]["gallery_images"]["Row"];
type TeamRow = Database["public"]["Tables"]["team_members"]["Row"];
type TestimonialRow = Database["public"]["Tables"]["testimonials"]["Row"];
type SubscriberRow = Database["public"]["Tables"]["subscribers"]["Row"];
type ContactRow = Database["public"]["Tables"]["contact_requests"]["Row"];
type SettingsRow = Database["public"]["Tables"]["site_settings"]["Row"];
type ContactStatus = Database["public"]["Enums"]["contact_status"];

const AdminLayout = ({ children, title }: { children: React.ReactNode; title: string }) => (
  <AdminShell title={title}>{children}</AdminShell>
);

const Spinner = () => (
  <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
);

// =========================================================
// GALLERY CRM
// =========================================================
export const AdminGallery = () => {
  const [rows, setRows] = useState<GalleryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("gallery_images")
      .select("*")
      .order("display_order")
      .order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    setRows(data ?? []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const ext = file.name.split(".").pop() || "jpg";
        const path = `${crypto.randomUUID()}.${ext}`;
        const { error: upErr } = await supabase.storage.from("gallery").upload(path, file, { cacheControl: "3600" });
        if (upErr) { toast.error(upErr.message); continue; }
        const { data: pub } = supabase.storage.from("gallery").getPublicUrl(path);
        const { error: dbErr } = await supabase.from("gallery_images").insert({
          storage_path: path,
          public_url: pub.publicUrl,
          title: file.name.replace(/\.[^.]+$/, ""),
          alt_text: file.name.replace(/\.[^.]+$/, ""),
        });
        if (dbErr) toast.error(dbErr.message);
      }
      toast.success("Upload complete");
      e.target.value = "";
      load();
    } finally {
      setUploading(false);
    }
  };

  const toggleFeatured = async (img: GalleryRow) => {
    const { error } = await supabase.from("gallery_images").update({ featured: !img.featured }).eq("id", img.id);
    if (error) return toast.error(error.message);
    load();
  };

  const del = async (img: GalleryRow) => {
    if (!confirm("Delete this image?")) return;
    await supabase.storage.from("gallery").remove([img.storage_path]);
    const { error } = await supabase.from("gallery_images").delete().eq("id", img.id);
    if (error) return toast.error(error.message);
    toast.success("Deleted");
    load();
  };

  return (
    <AdminLayout title="GALLERY">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <p className="text-muted-foreground font-body text-sm">{rows.length} images</p>
        <label className="cursor-pointer">
          <input type="file" accept="image/*" multiple onChange={onUpload} className="hidden" disabled={uploading} />
          <span className="inline-flex items-center bg-primary text-primary-foreground hover:bg-primary/90 font-body font-semibold px-6 h-10 text-sm">
            {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
            Upload Images
          </span>
        </label>
      </div>
      {loading ? <Spinner /> : rows.length === 0 ? (
        <div className="bg-panel border border-border p-6">
          <p className="text-muted-foreground font-body text-sm">No images yet. Upload some.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {rows.map((img) => (
            <div key={img.id} className="group relative bg-panel border border-border overflow-hidden">
              <img src={img.public_url} alt={img.alt_text ?? ""} className="w-full aspect-square object-cover" />
              <div className="absolute inset-0 bg-background/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <Button size="sm" variant="outline" onClick={() => toggleFeatured(img)} className="font-body text-xs">
                  {img.featured ? <StarOff className="w-3 h-3" /> : <Star className="w-3 h-3" />}
                </Button>
                <Button size="sm" variant="outline" onClick={() => del(img)} className="font-body text-xs text-destructive">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
              {img.featured && (
                <div className="absolute top-2 left-2 bg-primary text-primary-foreground text-[10px] font-mono uppercase tracking-wider px-2 py-0.5">Featured</div>
              )}
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

// =========================================================
// TEAM CRM
// =========================================================
type TeamDraft = {
  id?: string;
  name: string;
  role: string;
  bio: string;
  photo_url: string;
  active: boolean;
  display_order: number;
};
const emptyTeam: TeamDraft = { name: "", role: "", bio: "", photo_url: "", active: true, display_order: 0 };

export const AdminTeam = () => {
  const [rows, setRows] = useState<TeamRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<TeamDraft>(emptyTeam);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.from("team_members").select("*").order("display_order").order("created_at");
    if (error) toast.error(error.message);
    setRows(data ?? []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setDraft(emptyTeam); setOpen(true); };
  const openEdit = (m: TeamRow) => {
    setDraft({
      id: m.id, name: m.name, role: m.role ?? "", bio: m.bio ?? "",
      photo_url: m.photo_url ?? "", active: m.active, display_order: m.display_order,
    });
    setOpen(true);
  };

  const onPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const ext = file.name.split(".").pop() || "jpg";
    const path = `${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from("team").upload(path, file);
    if (error) { setUploading(false); return toast.error(error.message); }
    const { data: pub } = supabase.storage.from("team").getPublicUrl(path);
    setDraft({ ...draft, photo_url: pub.publicUrl });
    setUploading(false);
  };

  const save = async () => {
    if (!draft.name.trim()) return toast.error("Name required");
    setSaving(true);
    const payload = {
      name: draft.name.trim(),
      role: draft.role || null,
      bio: draft.bio || null,
      photo_url: draft.photo_url || null,
      active: draft.active,
      display_order: draft.display_order,
    };
    const { error } = draft.id
      ? await supabase.from("team_members").update(payload).eq("id", draft.id)
      : await supabase.from("team_members").insert(payload);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success(draft.id ? "Saved" : "Created");
    setOpen(false);
    load();
  };

  const del = async (id: string) => {
    if (!confirm("Delete this member?")) return;
    const { error } = await supabase.from("team_members").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Deleted");
    load();
  };

  return (
    <AdminLayout title="TEAM MEMBERS">
      <div className="flex justify-between items-center mb-6">
        <p className="text-muted-foreground font-body text-sm">{rows.length} members</p>
        <Button onClick={openNew} className="bg-primary text-primary-foreground hover:bg-primary/90 font-body font-semibold px-6">
          <Plus className="w-4 h-4 mr-1" /> Add Member
        </Button>
      </div>
      {loading ? <Spinner /> : rows.length === 0 ? (
        <div className="bg-panel border border-border p-6">
          <p className="text-muted-foreground font-body text-sm">No team members yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((m) => (
            <div key={m.id} className="bg-panel border border-border p-4 flex gap-3">
              <div className="w-16 h-16 bg-surface border border-border shrink-0 overflow-hidden">
                {m.photo_url && <img src={m.photo_url} alt={m.name} className="w-full h-full object-cover" />}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-body font-medium text-sm truncate">{m.name}</h3>
                <p className="text-xs text-muted-foreground font-mono uppercase mt-0.5">{m.role || "—"}</p>
                <div className="flex gap-1 mt-2">
                  <Button variant="outline" size="sm" onClick={() => openEdit(m)} className="font-body text-xs h-7">
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => del(m.id)} className="font-body text-xs h-7 text-destructive">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display tracking-widest">{draft.id ? "EDIT MEMBER" : "NEW MEMBER"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Name</Label>
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Role</Label>
              <Input value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })} />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Photo</Label>
              <div className="flex gap-3 items-center">
                {draft.photo_url && <img src={draft.photo_url} alt="" className="w-16 h-16 object-cover border border-border" />}
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" onChange={onPhoto} className="hidden" disabled={uploading} />
                  <span className="inline-flex items-center bg-surface border border-border px-3 h-9 text-xs font-body">
                    {uploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                    Upload
                  </span>
                </label>
              </div>
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Bio</Label>
              <Textarea rows={4} value={draft.bio} onChange={(e) => setDraft({ ...draft, bio: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-mono text-xs uppercase tracking-widest">Order</Label>
                <Input type="number" value={draft.display_order} onChange={(e) => setDraft({ ...draft, display_order: Number(e.target.value) })} />
              </div>
              <div className="flex items-end gap-2">
                <Switch checked={draft.active} onCheckedChange={(v) => setDraft({ ...draft, active: v })} />
                <Label className="font-mono text-xs uppercase tracking-widest mb-2">Active</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90">
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {draft.id ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

// =========================================================
// TESTIMONIALS CRM
// =========================================================
type TestiDraft = {
  id?: string;
  customer_name: string;
  project_type: string;
  quote: string;
  rating: number;
  customer_photo: string;
  featured: boolean;
  display_order: number;
};
const emptyTesti: TestiDraft = { customer_name: "", project_type: "", quote: "", rating: 5, customer_photo: "", featured: false, display_order: 0 };

export const AdminTestimonials = () => {
  const [rows, setRows] = useState<TestimonialRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<TestiDraft>(emptyTesti);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.from("testimonials").select("*").order("display_order").order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    setRows(data ?? []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setDraft(emptyTesti); setOpen(true); };
  const openEdit = (t: TestimonialRow) => {
    setDraft({
      id: t.id, customer_name: t.customer_name, project_type: t.project_type ?? "",
      quote: t.quote, rating: t.rating ?? 5, customer_photo: t.customer_photo ?? "",
      featured: t.featured, display_order: t.display_order,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!draft.customer_name.trim() || !draft.quote.trim()) return toast.error("Name and quote required");
    setSaving(true);
    const payload = {
      customer_name: draft.customer_name.trim(),
      project_type: draft.project_type || null,
      quote: draft.quote.trim(),
      rating: draft.rating,
      customer_photo: draft.customer_photo || null,
      featured: draft.featured,
      display_order: draft.display_order,
    };
    const { error } = draft.id
      ? await supabase.from("testimonials").update(payload).eq("id", draft.id)
      : await supabase.from("testimonials").insert(payload);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success(draft.id ? "Saved" : "Created");
    setOpen(false);
    load();
  };

  const del = async (id: string) => {
    if (!confirm("Delete this testimonial?")) return;
    const { error } = await supabase.from("testimonials").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Deleted");
    load();
  };

  return (
    <AdminLayout title="TESTIMONIALS">
      <div className="flex justify-between items-center mb-6">
        <p className="text-muted-foreground font-body text-sm">{rows.length} testimonials</p>
        <Button onClick={openNew} className="bg-primary text-primary-foreground hover:bg-primary/90 font-body font-semibold px-6">
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </div>
      {loading ? <Spinner /> : rows.length === 0 ? (
        <div className="bg-panel border border-border p-6"><p className="text-muted-foreground font-body text-sm">No testimonials yet.</p></div>
      ) : (
        <div className="space-y-3">
          {rows.map((t) => (
            <div key={t.id} className="bg-panel border border-border p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-body text-sm italic">"{t.quote}"</p>
                  <p className="text-xs text-muted-foreground font-mono uppercase mt-2">
                    — {t.customer_name} {t.project_type ? `· ${t.project_type}` : ""} · {"★".repeat(t.rating ?? 0)}
                    {t.featured && <span className="text-primary ml-2">FEATURED</span>}
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button variant="outline" size="sm" onClick={() => openEdit(t)} className="font-body text-xs h-7"><Pencil className="w-3 h-3" /></Button>
                  <Button variant="outline" size="sm" onClick={() => del(t.id)} className="font-body text-xs h-7 text-destructive"><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display tracking-widest">{draft.id ? "EDIT" : "NEW"} TESTIMONIAL</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Customer Name</Label>
              <Input value={draft.customer_name} onChange={(e) => setDraft({ ...draft, customer_name: e.target.value })} />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Project Type</Label>
              <Input value={draft.project_type} onChange={(e) => setDraft({ ...draft, project_type: e.target.value })} placeholder="e.g. Music Video, Photoshoot" />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Quote</Label>
              <Textarea rows={4} value={draft.quote} onChange={(e) => setDraft({ ...draft, quote: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-mono text-xs uppercase tracking-widest">Rating (1-5)</Label>
                <Input type="number" min={1} max={5} value={draft.rating} onChange={(e) => setDraft({ ...draft, rating: Number(e.target.value) })} />
              </div>
              <div>
                <Label className="font-mono text-xs uppercase tracking-widest">Order</Label>
                <Input type="number" value={draft.display_order} onChange={(e) => setDraft({ ...draft, display_order: Number(e.target.value) })} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={draft.featured} onCheckedChange={(v) => setDraft({ ...draft, featured: v })} />
              <Label className="font-mono text-xs uppercase tracking-widest">Featured</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90">
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {draft.id ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

// =========================================================
// SUBSCRIBERS
// =========================================================
export const AdminSubscribers = () => {
  const [rows, setRows] = useState<SubscriberRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.from("subscribers").select("*").order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    setRows(data ?? []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const exportCsv = () => {
    const csv = ["email,source,subscribed_at,unsubscribed_at",
      ...rows.map(r => `${r.email},${r.source ?? ""},${r.created_at},${r.unsubscribed_at ?? ""}`)
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `subscribers-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout title="SUBSCRIBERS">
      <div className="flex justify-between items-center mb-6">
        <p className="text-muted-foreground font-body text-sm">
          {rows.filter(r => !r.unsubscribed_at).length} active · {rows.length} total
        </p>
        <Button onClick={exportCsv} variant="outline" className="font-body" disabled={!rows.length}>Export CSV</Button>
      </div>
      {loading ? <Spinner /> : rows.length === 0 ? (
        <div className="bg-panel border border-border p-6"><p className="text-muted-foreground font-body text-sm">No subscribers yet.</p></div>
      ) : (
        <div className="bg-panel border border-border overflow-hidden">
          {rows.map((s, i) => (
            <div key={s.id} className={`p-4 flex items-center justify-between gap-3 ${i < rows.length - 1 ? "border-b border-border" : ""}`}>
              <div className="min-w-0">
                <p className="font-body text-sm truncate">{s.email}</p>
                <p className="text-xs text-muted-foreground font-mono uppercase mt-0.5">
                  {s.source ?? "direct"} · {new Date(s.created_at).toLocaleDateString()}
                  {s.unsubscribed_at && <span className="text-destructive ml-2">UNSUBSCRIBED</span>}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

// =========================================================
// CONTACT REQUESTS
// =========================================================
export const AdminContact = () => {
  const [rows, setRows] = useState<ContactRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<ContactRow | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.from("contact_requests").select("*").order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    setRows(data ?? []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id: string, status: ContactStatus) => {
    const { error } = await supabase.from("contact_requests").update({ status }).eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Updated");
    load();
    if (open?.id === id) setOpen({ ...open, status });
  };

  const del = async (id: string) => {
    if (!confirm("Delete this request?")) return;
    const { error } = await supabase.from("contact_requests").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Deleted");
    setOpen(null);
    load();
  };

  return (
    <AdminLayout title="CONTACT REQUESTS">
      <p className="text-muted-foreground font-body text-sm mb-6">
        {rows.filter(r => r.status === "new").length} new · {rows.length} total
      </p>
      {loading ? <Spinner /> : rows.length === 0 ? (
        <div className="bg-panel border border-border p-6"><p className="text-muted-foreground font-body text-sm">No requests yet.</p></div>
      ) : (
        <div className="bg-panel border border-border overflow-hidden">
          {rows.map((r, i) => (
            <button key={r.id} onClick={() => setOpen(r)}
              className={`w-full p-4 text-left hover:bg-surface transition-colors ${i < rows.length - 1 ? "border-b border-border" : ""}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-body text-sm">{r.full_name} <span className="text-muted-foreground">· {r.email}</span></p>
                  <p className="text-xs text-muted-foreground font-mono mt-1 truncate">{r.message}</p>
                </div>
                <span className={`text-xs font-mono uppercase tracking-wider px-2 py-1 shrink-0 ${
                  r.status === "new" ? "bg-primary/20 text-primary" : "bg-surface text-muted-foreground"
                }`}>{r.status}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {open && (
            <>
              <DialogHeader><DialogTitle className="font-display tracking-widest">{open.full_name.toUpperCase()}</DialogTitle></DialogHeader>
              <div className="space-y-3 text-sm font-body">
                <p><span className="text-muted-foreground font-mono uppercase text-xs">Email:</span> <a href={`mailto:${open.email}`} className="text-primary">{open.email}</a></p>
                {open.phone && <p><span className="text-muted-foreground font-mono uppercase text-xs">Phone:</span> {open.phone}</p>}
                {open.service && <p><span className="text-muted-foreground font-mono uppercase text-xs">Service:</span> {open.service}</p>}
                <p className="text-muted-foreground font-mono uppercase text-xs">Message:</p>
                <p className="whitespace-pre-wrap bg-surface border border-border p-3">{open.message}</p>
                <div>
                  <Label className="font-mono text-xs uppercase tracking-widest">Status</Label>
                  <Select value={open.status} onValueChange={(v) => updateStatus(open.id, v as ContactStatus)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="replied">Replied</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => del(open.id)} className="text-destructive">Delete</Button>
                <Button onClick={() => setOpen(null)} className="bg-primary text-primary-foreground hover:bg-primary/90">Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

// =========================================================
// SITE SETTINGS
// =========================================================
type SettingsDraft = {
  hero_headline: string;
  hero_subheadline: string;
  email: string;
  phone: string;
  address: string;
  footer_copy: string;
  google_maps_embed: string;
  socials: { instagram?: string; facebook?: string; tiktok?: string; youtube?: string };
  hours: { weekday?: string; weekend?: string };
};

export const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<SettingsDraft>({
    hero_headline: "", hero_subheadline: "", email: "", phone: "", address: "",
    footer_copy: "", google_maps_embed: "", socials: {}, hours: {},
  });

  const load = async () => {
    const { data, error } = await supabase.from("site_settings").select("*").eq("id", 1).maybeSingle();
    if (error) toast.error(error.message);
    if (data) {
      const s = data as SettingsRow;
      setDraft({
        hero_headline: s.hero_headline ?? "",
        hero_subheadline: s.hero_subheadline ?? "",
        email: s.email ?? "",
        phone: s.phone ?? "",
        address: s.address ?? "",
        footer_copy: s.footer_copy ?? "",
        google_maps_embed: s.google_maps_embed ?? "",
        socials: (s.socials as SettingsDraft["socials"]) ?? {},
        hours: (s.hours as SettingsDraft["hours"]) ?? {},
      });
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    const { error } = await supabase.from("site_settings").update({
      hero_headline: draft.hero_headline || null,
      hero_subheadline: draft.hero_subheadline || null,
      email: draft.email || null,
      phone: draft.phone || null,
      address: draft.address || null,
      footer_copy: draft.footer_copy || null,
      google_maps_embed: draft.google_maps_embed || null,
      socials: draft.socials,
      hours: draft.hours,
    }).eq("id", 1);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success("Settings saved");
  };

  if (loading) return <AdminLayout title="SITE SETTINGS"><Spinner /></AdminLayout>;

  return (
    <AdminLayout title="SITE SETTINGS">
      <div className="max-w-2xl space-y-8">
        <section className="bg-panel border border-border p-6 space-y-4">
          <h2 className="font-display tracking-widest text-sm">HERO</h2>
          <div>
            <Label className="font-mono text-xs uppercase tracking-widest">Headline</Label>
            <Input value={draft.hero_headline} onChange={(e) => setDraft({ ...draft, hero_headline: e.target.value })} />
          </div>
          <div>
            <Label className="font-mono text-xs uppercase tracking-widest">Subheadline</Label>
            <Textarea rows={2} value={draft.hero_subheadline} onChange={(e) => setDraft({ ...draft, hero_subheadline: e.target.value })} />
          </div>
        </section>

        <section className="bg-panel border border-border p-6 space-y-4">
          <h2 className="font-display tracking-widest text-sm">CONTACT</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Email</Label>
              <Input value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Phone</Label>
              <Input value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} />
            </div>
          </div>
          <div>
            <Label className="font-mono text-xs uppercase tracking-widest">Address</Label>
            <Textarea rows={2} value={draft.address} onChange={(e) => setDraft({ ...draft, address: e.target.value })} />
          </div>
          <div>
            <Label className="font-mono text-xs uppercase tracking-widest">Google Maps Embed (iframe URL)</Label>
            <Input value={draft.google_maps_embed} onChange={(e) => setDraft({ ...draft, google_maps_embed: e.target.value })} />
          </div>
        </section>

        <section className="bg-panel border border-border p-6 space-y-4">
          <h2 className="font-display tracking-widest text-sm">HOURS</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Weekday</Label>
              <Input value={draft.hours.weekday ?? ""} onChange={(e) => setDraft({ ...draft, hours: { ...draft.hours, weekday: e.target.value } })} placeholder="Mon-Fri 9am-9pm" />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Weekend</Label>
              <Input value={draft.hours.weekend ?? ""} onChange={(e) => setDraft({ ...draft, hours: { ...draft.hours, weekend: e.target.value } })} placeholder="Sat-Sun 10am-6pm" />
            </div>
          </div>
        </section>

        <section className="bg-panel border border-border p-6 space-y-4">
          <h2 className="font-display tracking-widest text-sm">SOCIALS</h2>
          {(["instagram","facebook","tiktok","youtube"] as const).map((k) => (
            <div key={k}>
              <Label className="font-mono text-xs uppercase tracking-widest">{k}</Label>
              <Input value={draft.socials[k] ?? ""} onChange={(e) => setDraft({ ...draft, socials: { ...draft.socials, [k]: e.target.value } })} placeholder="https://…" />
            </div>
          ))}
        </section>

        <section className="bg-panel border border-border p-6 space-y-4">
          <h2 className="font-display tracking-widest text-sm">FOOTER</h2>
          <div>
            <Label className="font-mono text-xs uppercase tracking-widest">Footer copy</Label>
            <Textarea rows={3} value={draft.footer_copy} onChange={(e) => setDraft({ ...draft, footer_copy: e.target.value })} />
          </div>
        </section>

        <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90 font-body font-semibold px-8">
          {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
          Save All Settings
        </Button>

        <GoogleCalendarPanel />
      </div>
    </AdminLayout>
  );
};

// =========================================================
// GOOGLE CALENDAR INTEGRATION (Phase D)
// =========================================================
type CalIntegRow = Database["public"]["Tables"]["calendar_integrations"]["Row"];
type StudioRow = Database["public"]["Tables"]["studios"]["Row"];

const GoogleCalendarPanel = () => {
  const [integ, setInteg] = useState<CalIntegRow | null>(null);
  const [studios, setStudios] = useState<StudioRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [defaultCal, setDefaultCal] = useState("");
  const [studioMap, setStudioMap] = useState<Record<string, string>>({});

  const load = async () => {
    setLoading(true);
    const [i, s] = await Promise.all([
      supabase.from("calendar_integrations").select("*").eq("provider", "google").maybeSingle(),
      supabase.from("studios").select("*").order("display_order"),
    ]);
    if (i.data) {
      setInteg(i.data as CalIntegRow);
      setDefaultCal((i.data as CalIntegRow).default_calendar_id ?? "primary");
      setStudioMap(((i.data as CalIntegRow).studio_calendar_map as Record<string, string>) ?? {});
    }
    if (s.data) setStudios(s.data as StudioRow[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // Show toast on OAuth return
    const params = new URLSearchParams(window.location.search);
    if (params.get("gcal") === "connected") {
      toast.success("Google Calendar connected");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const connect = async () => {
    setBusy(true);
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { toast.error("Not signed in"); setBusy(false); return; }
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gcal-oauth/initiate?return_to=${encodeURIComponent(window.location.origin + "/admin/settings")}`,
      { headers: { Authorization: `Bearer ${session.access_token}` } }
    );
    const data = await res.json();
    setBusy(false);
    if (data.url) window.location.href = data.url;
    else toast.error(data.error || "Failed to start OAuth");
  };

  const disconnect = async () => {
    if (!confirm("Disconnect Google Calendar?")) return;
    setBusy(true);
    const { data: { session } } = await supabase.auth.getSession();
    await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gcal-oauth/disconnect`, {
      method: "POST",
      headers: { Authorization: `Bearer ${session?.access_token}` },
    });
    setInteg(null);
    setBusy(false);
    toast.success("Disconnected");
  };

  const saveMapping = async () => {
    if (!integ) return;
    setBusy(true);
    const { error } = await supabase.from("calendar_integrations").update({
      default_calendar_id: defaultCal || "primary",
      studio_calendar_map: studioMap,
    }).eq("id", integ.id);
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Calendar mapping saved");
    load();
  };

  const resync = async () => {
    setBusy(true);
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gcal-sync`, {
      method: "POST",
      headers: { Authorization: `Bearer ${session?.access_token}` },
    });
    const data = await res.json();
    setBusy(false);
    if (data.ok) {
      toast.success("Sync complete");
      load();
    } else {
      toast.error(data.error || "Sync failed");
    }
  };

  return (
    <section className="bg-panel border border-border p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display tracking-widest text-sm">GOOGLE CALENDAR SYNC</h2>
        {integ && (
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Last sync: {integ.last_sync_at ? new Date(integ.last_sync_at).toLocaleString() : "never"}
          </span>
        )}
      </div>

      {loading ? (
        <Spinner />
      ) : !integ ? (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Connect your Google account so bookings push to Google Calendar and external events block your booking grid. Polls every 5 minutes.
          </p>
          <Button onClick={connect} disabled={busy} className="bg-primary text-primary-foreground hover:bg-primary/90 font-body font-semibold">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            Connect Google Calendar
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <Label className="font-mono text-xs uppercase tracking-widest">Default calendar ID</Label>
            <Input value={defaultCal} onChange={(e) => setDefaultCal(e.target.value)} placeholder="primary or calendar_id@group.calendar.google.com" />
            <p className="text-xs text-muted-foreground mt-1">Use "primary" or paste a specific Google Calendar ID.</p>
          </div>

          {studios.length > 0 && (
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-widest">Per-studio calendar IDs (optional)</Label>
              {studios.map((st) => (
                <div key={st.id} className="grid grid-cols-[160px_1fr] gap-2 items-center">
                  <span className="font-mono text-xs">{st.name}</span>
                  <Input
                    value={studioMap[st.id] ?? ""}
                    onChange={(e) => setStudioMap({ ...studioMap, [st.id]: e.target.value })}
                    placeholder="(uses default)"
                  />
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            <Button onClick={saveMapping} disabled={busy} variant="outline" className="font-body font-semibold">
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Save Mapping
            </Button>
            <Button onClick={resync} disabled={busy} variant="outline" className="font-body font-semibold">
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Resync Now
            </Button>
            <Button onClick={disconnect} disabled={busy} variant="destructive" className="font-body font-semibold">
              Disconnect
            </Button>
          </div>
        </div>
      )}
    </section>
  );
};
